import { motion } from "framer-motion";
import { Eye, Sparkles, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import FeedbackButtons, { SUGGESTION_REASONS } from "@/components/FeedbackButtons";
import AgentQuestionCard from "@/components/AgentQuestionCard";
import { getTypeMeta, ageLabel } from "./streamModel";

const STATUS_TEXT = { executed: "Executed", answered: "Answered" };
const PULSE_DOTS = ["dot-a", "dot-b", "dot-c"];
const SLIDE_IN_FROM_LEFT = { opacity: 0, x: -20 };
const SLIDE_SETTLED = { opacity: 1, x: 0 };
const SLIDE_OUT_RIGHT = { opacity: 0, x: 20 };
const FADE_FROM_BELOW = { opacity: 0, y: 10 };
const FADE_SETTLED = { opacity: 1, y: 0 };
const QUICK = { duration: 0.3 };
const HIDDEN = { opacity: 0 };
const SHOWN = { opacity: 1 };
const SLOW = { duration: 0.5 };
const WOBBLE = { scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] };
const WOBBLE_LOOP = { duration: 3, repeat: Infinity };
const PULSE = { opacity: [0.3, 1, 0.3] };

export const StreamQuestionCard = ({ update, idx, userId, onAnswered }) => (
  <motion.div initial={SLIDE_IN_FROM_LEFT} animate={SLIDE_SETTLED} exit={SLIDE_OUT_RIGHT} transition={QUICK}>
    <AgentQuestionCard question={update.question} userId={userId} questionId={update.questionId || `q-${idx}`} onAnswered={onAnswered} />
  </motion.div>
);

// Source line under a news/research card: publication age + clickable article link
const SourceLine = ({ update, idx }) => {
  const news = update.fullData?.newsItems?.[0];
  const source = news || update.fullData?.sources?.[0];
  if (!source?.url) return null;
  const age = ageLabel(news?.publishedAt || update.fullData?.publishedAt);
  return (
    <a href={source.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 text-xs text-ism-teal hover:underline mt-2" data-testid={`stream-source-link-${idx}`}>
      <ExternalLink className="h-3 w-3" />
      {source.source || "Source"}{age ? ` · ${age}` : ""}
    </a>
  );
};

export const StreamCard = ({ update, idx, userId, onReview }) => {
  const meta = getTypeMeta(update.priority, update.status);
  const isNew = update.status === "new";
  const leadCount = update.priority === "prospect" ? (update.fullData?.companiesFound?.length || update.fullData?.totalResults || 0) : 0;
  return (
    <motion.div
      initial={FADE_FROM_BELOW}
      animate={FADE_SETTLED}
      exit={SLIDE_OUT_RIGHT}
      transition={{ ...QUICK, delay: idx * 0.05 }}
      className={`glass-card-light rounded-xl p-4 border border-white/10 hover:border-ism-teal/30 transition-all ${isNew ? "" : "opacity-70"}`}
      data-testid={`hud-update-${idx}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs font-medium uppercase tracking-wider text-ism-teal flex items-center gap-1.5 min-w-0">
          <meta.Icon className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{meta.label}</span>
        </span>
        {meta.StatusIcon && !isNew ? (
          <span className={`flex items-center gap-1 text-xs flex-shrink-0 ${meta.statusColor}`}>
            <meta.StatusIcon className="h-3 w-3" />
            {STATUS_TEXT[update.status] || "Viewed"}
          </span>
        ) : isNew && (
          <span className="text-xs px-2 py-0.5 rounded-full border bg-ism-teal/10 text-ism-teal border-ism-teal/20 flex-shrink-0">NEW</span>
        )}
      </div>

      <p className={`text-sm leading-relaxed ${isNew ? "text-white" : "text-gray-400"}`}>
        {leadCount > 0 ? `${leadCount} new leads found matching your ICP` : update.text}
      </p>
      {leadCount > 0 && <p className="text-xs text-gray-500 mt-1">Click Review to save or dismiss leads</p>}
      <SourceLine update={update} idx={idx} />

      <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-white/5">
        <FeedbackButtons
          userId={userId}
          agentType="autonomous_stream"
          actionId={update.fullData?.actionId}
          outputSummary={update.text}
          context={{ type: update.type, kind: "suggestion" }}
          reasons={SUGGESTION_REASONS}
          testIdPrefix={`stream-${idx}`}
        />
        {update.hasButton && (
          <Button onClick={() => onReview(update, idx)} size="sm" variant="outline" data-testid={`review-btn-${idx}`}
            className={`text-xs h-7 border-white/20 hover:border-white/40 ${isNew ? "text-ism-teal border-ism-teal/30" : "text-gray-400 hover:text-white"}`}>
            <Eye className="h-3 w-3 mr-1" />
            Review
          </Button>
        )}
      </div>
    </motion.div>
  );
};

export const StreamEmptyState = ({ activeFilter }) => (
  <motion.div className="text-center py-16" initial={HIDDEN} animate={SHOWN} transition={SLOW}>
    <motion.div animate={WOBBLE} transition={WOBBLE_LOOP} className="inline-block">
      <div className="p-6 rounded-2xl bg-gradient-to-br from-ism-teal/10 to-ism-teal/10 border border-white/10 inline-block">
        <Sparkles className="h-12 w-12 text-ism-teal" />
      </div>
    </motion.div>
    <p className="text-gray-400 mt-6 font-medium">{activeFilter === "all" ? "Autonomous agent initializing..." : `No ${activeFilter} items yet`}</p>
    <p className="text-gray-600 text-sm mt-2">{activeFilter === "all" ? "First activity will appear shortly" : "Items will appear here as they're generated"}</p>
    <div className="flex justify-center gap-1.5 mt-4">
      {PULSE_DOTS.map((id, i) => (
        <motion.div key={id} className="w-2 h-2 rounded-full bg-ism-teal" animate={PULSE} transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }} />
      ))}
    </div>
  </motion.div>
);
